import { request } from '@/api/client';
import { SHOW_PAYMENTS } from '@/pro';

export type Tier = 'free' | 'mahalla' | 'shahar' | 'trassa';
export type PaidTier = Exclude<Tier, 'free'>;
export type PayMethod = 'click' | 'payme' | 'karta';
export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'cancelled' | 'timeout';

export const TIER_RANK: Record<Tier, number> = { free: 0, mahalla: 1, shahar: 2, trassa: 3 };
export const PLAN_PRICE: Record<PaidTier, number> = {
  mahalla: 88000,
  shahar: 188000,
  trassa: 288000,
};
export const PERIOD_DAYS = [7, 31, 93, 365] as const;
export const PERIOD_MULT = [0.3, 1, 2.7, 9] as const;
export const PAY_METHODS: PayMethod[] = ['click', 'payme', 'karta'];
export const DELETE_DAYS = 7;
export const DAY_MS = 864e5;

export interface Plan {
  tier: Tier;
  until: string | null;
  previous: Tier | null;
  source?: 'personal' | 'everyone' | 'free';
}

export interface Payment {
  id: string;
  at: string;
  method: PayMethod;
  plan: PaidTier;
  period: number;
  sum: number;
  promo: string | null;
  status: PaymentStatus;
  manual: boolean;
}

export interface NewPayment {
  plan: PaidTier;
  period: number;
  method: PayMethod;
  promo: string | null;
}

export interface CardDetails {
  number: string;
  holder: string;
}

export class PaymentError extends Error {
  constructor(readonly code: 'lower_plan' | 'promo_bad' | 'promo_used' | 'promo_empty') {
    super(code);
  }
}

export interface PaymentsApi {
  getPlan(): Promise<Plan>;
  getPending(): Promise<Payment | null>;
  createPayment(input: NewPayment): Promise<Payment>;
  checkPayment(id: string): Promise<Payment>;
  listPayments(): Promise<Payment[]>;
  checkPromo(code: string): Promise<number>;
  cardDetails(): Promise<CardDetails>;
  requestCallback(channel: 'tg' | 'sms'): Promise<void>;
  getDeletion(): Promise<string | null>;
  requestDeletion(): Promise<string>;
  cancelDeletion(): Promise<void>;
}

export const roundSum = (n: number) => Math.round(n / 1000) * 1000;

export function periodSum(plan: PaidTier, period: number, promoPct = 0): number {
  const base = roundSum(PLAN_PRICE[plan] * PERIOD_MULT[period]);
  return promoPct > 0 ? roundSum(base * (1 - promoPct)) : base;
}

export function daysLeft(until: string | null, now = Date.now()): number {
  if (!until) return 0;
  return Math.max(0, Math.ceil((Date.parse(until) - now) / DAY_MS));
}

const load = () => import('@/api/paymentsMock').then((m) => m.mockPayments);

const FREE_PLAN: Plan = { tier: 'free', until: null, previous: null };

function withMockPayments(): Promise<Plan> {
  return Promise.all([
    request<Plan>('/me/plan').catch(() => FREE_PLAN),
    load().then((api) => api.getPlan()),
  ]).then(([server, paid]) => (TIER_RANK[paid.tier] > TIER_RANK[server.tier] ? paid : server));
}

interface DeletionReply {
  delete_at: string | null;
}

export const payments: PaymentsApi = {
  getPlan: () => (SHOW_PAYMENTS ? withMockPayments() : request<Plan>('/me/plan')),
  getPending: () => load().then((api) => api.getPending()),
  createPayment: (input) => load().then((api) => api.createPayment(input)),
  checkPayment: (id) => load().then((api) => api.checkPayment(id)),
  listPayments: () => load().then((api) => api.listPayments()),
  checkPromo: (code) => load().then((api) => api.checkPromo(code)),
  cardDetails: () => load().then((api) => api.cardDetails()),
  requestCallback: (channel) => load().then((api) => api.requestCallback(channel)),
  getDeletion: () => request<DeletionReply>('/me/deletion').then((r) => r.delete_at),
  requestDeletion: () =>
    request<DeletionReply>('/me/deletion', { method: 'POST' }).then((r) => r.delete_at ?? ''),
  cancelDeletion: () => request('/me/deletion', { method: 'DELETE' }),
};
